import { Fragment, useEffect, useRef } from "react";
import { UseFormRegister, UseFormSetValue, UseFormWatch } from "react-hook-form";
import { Menu, Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { TextInput } from "../shared/Inputs/TextInput";
import ComboBoxInput from "../shared/Inputs/ComboBoxInput";
import { IWarehouseLocation } from "../../models/Location";
import classNames from "../../utils/classNames";

type LocationSearchMode = "equals" | "beginsWith";

interface WarehouseLocationSearchProps {
  showSearchOptions?: boolean;
  register: UseFormRegister<any>;
  setValue: UseFormSetValue<any>;
  watch: UseFormWatch<any>;
  isSearchDisabled?: boolean;
  warehouseLocations: {
    data?: { data: IWarehouseLocation[] };
    isLoading: boolean;
  };
}

export default function WarehouseLocationSearch({
  showSearchOptions = true,
  register,
  setValue,
  watch,
  isSearchDisabled = false,
  warehouseLocations,
}: WarehouseLocationSearchProps) {
  const locationSearchMode = watch("locationSearchMode") as LocationSearchMode;
  const prevModeRef = useRef<LocationSearchMode>(locationSearchMode);

  // Clear the appropriate input when search mode changes
  useEffect(() => {
    if (prevModeRef.current !== locationSearchMode) {
      if (locationSearchMode === "equals") {
        // Clear the "beginsWith" field when switching to "equals"
        setValue("warehouseLocationBeginsWith", "");
      } else if (locationSearchMode === "beginsWith") {
        // Clear the "equals" field when switching to "beginsWith"
        setValue("warehouseLocation", undefined);
      }
      prevModeRef.current = locationSearchMode;
    }
  }, [locationSearchMode, setValue]);

  const handleModeChange = (newMode: LocationSearchMode) => {
    setValue("locationSearchMode", newMode);
    
    // Clear the appropriate field immediately
    if (newMode === "equals") {
      setValue("warehouseLocationBeginsWith", "");
    } else if (newMode === "beginsWith") {
      setValue("warehouseLocation", undefined);
    }
  };

  const getModeLabel = () => {
    return locationSearchMode === "equals" ? "Equals" : "Begins with";
  };

  return (
    <div className="flex flex-col">
      <label className="block text-sm font-medium text-gray-700 mb-1">Warehouse Location</label>
      <div className="relative flex items-center">
        {showSearchOptions && (
          <Menu as="div" className="relative">
            <Menu.Button
              disabled={isSearchDisabled}
              className={classNames(
                "flex h-10 items-center justify-center gap-x-1 rounded-l-md border border-r-0 border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              )}
            >
              <span>{getModeLabel()}</span>
              <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute left-0 mt-2 w-40 origin-top-left rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
                <div className="py-1">
                  {(["equals", "beginsWith"] as LocationSearchMode[]).map((mode) => (
                    <Menu.Item key={mode}>
                      {({ active }) => (
                        <button
                          type="button"
                          onClick={() => handleModeChange(mode)}
                          className={classNames(
                            active ? "bg-gray-100" : "",
                            locationSearchMode === mode ? "bg-gray-50 font-medium" : "",
                            "w-full px-4 py-2 text-left text-sm text-gray-700"
                          )}
                        >
                          {mode === "equals" ? "Equals" : "Begins with"}
                        </button>
                      )}
                    </Menu.Item>
                  ))}
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
        )}
        <div className={classNames(
          "relative flex-1",
          showSearchOptions ? "" : "rounded-l-md"
        )}>
          {locationSearchMode === "beginsWith" ? (
            <div className="relative w-full text-gray-400 focus-within:text-gray-600">
              <input
                {...register("warehouseLocationBeginsWith")}
                placeholder="e.g. A1, Bay"
                disabled={isSearchDisabled}
                className={classNames(
                  "block h-10 w-full border border-gray-300 py-2 px-3 text-gray-900 placeholder-gray-500 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm",
                  showSearchOptions ? "border-l-0 rounded-r-md" : "rounded-md"
                )}
              />
            </div>
          ) : (
            <div className={classNames(
              "min-w-96",
              showSearchOptions 
                ? "[&_input]:h-10 [&_input]:border-l-0 [&_input]:rounded-r-md [&>div>div>div]:h-10 -mt-1" 
                : ""
            )}>
              <ComboBoxInput
                label=""
                register={register}
                registerName={"warehouseLocation"}
                options={warehouseLocations.data?.data ?? []}
                setValue={setValue}
                disabled={isSearchDisabled || warehouseLocations.isLoading}
                searchField={"displayName"}
                classes="min-w-96"
                placeholder="Start Typing to Search Warehouse Locations"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
